
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { getAssessmentsSummary, AssessmentSummary } from '@/services/assessment/getAssessments';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

interface DashboardResponse {
  id: string;
  assessment_id: string;
  submitted_at: string;
  personal_info: any;
}

const fetchDashboardResponses = async (): Promise<DashboardResponse[]> => {
  const { data, error } = await supabase
    .from("responses")
    .select("id, assessment_id, submitted_at, personal_info")
    .order("submitted_at", { ascending: false })
    .limit(100);

  if (error) throw error;
  return data || [];
};

export default function Dashboard() {
  const navigate = useNavigate();
  
  const { data: assessments = [], isLoading: loadingAssessments } = useQuery({
    queryKey: ['assessments-summary'],
    queryFn: getAssessmentsSummary,
  });

  const { data: responses = [], isLoading: loadingResponses } = useQuery({
    queryKey: ['dashboard-responses'],
    queryFn: fetchDashboardResponses,
  });

  const { data: pendingRequestsCount = 0 } = useQuery({
    queryKey: ['premium-requests-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('premium_access_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      if (error) throw error;
      return count || 0;
    },
  });

  const loading = loadingAssessments || loadingResponses;

  const activeAssessments = assessments.filter(a => a.isActive).length;
  const totalResponses = responses.length;

  const recentResponses = responses.slice(0, 5);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Assessments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{assessments.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Assessments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{activeAssessments}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Submissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalResponses}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Completion Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {totalResponses > 0 ? '100%' : '0%'}
            </div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/admin/premium-requests')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Full Access Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{pendingRequestsCount}</div>
            <p className="text-xs text-muted-foreground mt-1">pending</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Recent Submissions</CardTitle>
            <CardDescription>
              Latest assessment submissions by users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentResponses.length > 0 ? (
                recentResponses.map((response) => {
                  const assessment = assessments.find(a => a.id === response.assessment_id);
                  const personalInfo = typeof response.personal_info === 'string'
                    ? JSON.parse(response.personal_info)
                    : response.personal_info;
                  return (
                    <div key={response.id} className="border-b pb-3 last:border-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{personalInfo?.fullName || personalInfo?.name || 'Anonymous'}</div>
                          <div className="text-sm text-muted-foreground">
                            {assessment?.title}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(response.submitted_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  No submissions yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Assessment Overview</CardTitle>
            <CardDescription>
              Summary of your current assessments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {assessments.length > 0 ? (
                assessments.slice(0, 5).map((assessment) => (
                  <div key={assessment.id} className="border-b pb-3 last:border-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{assessment.title}</div>
                        <div className="text-sm text-muted-foreground">
                          {assessment.categoryCount} categories
                        </div>
                      </div>
                      <div className="text-xs">
                        <span className={`px-2 py-1 rounded-full ${assessment.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {assessment.isActive ? 'Active' : 'Archived'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  No assessments created yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
